type SqlExecutor = (sql: string, params?: unknown[]) => Promise<{ rows: any[] }>;

export type DedupeSeedCategoriesResult = {
    /** Categorias duplicadas absorvidas (soft-deletadas depois do repoint). */
    mergedCategories: number;
    /** Subcategorias duplicadas absorvidas dentro da categoria sobrevivente. */
    mergedSubcategories: number;
};

/**
 * Cura retroativa da DUPLICAÇÃO DO CATÁLOGO DE FÁBRICA (ADR 0119) — roda no boot.
 *
 * O `seedDatabase` decidia semear por `count()` e plantava fora de transação: dois
 * `prepare()` concorrentes do layout raiz (remontagem da Activity durante o primeiro
 * boot) liam ambos `count === 0` e plantavam o catálogo INTEIRO duas vezes — 22
 * categorias repetidas, cada uma com suas subcategorias, todas com o mesmo
 * `sort_order`, o que na tela vira o par colado ("Moradia, Moradia, Alimentação,
 * Alimentação..."). O `seedDatabase` já não permite um caso novo; isto conserta quem
 * ficou com o banco sujo, e nenhuma outra origem recria o par: o backfill de
 * "Serviços" respeita o tombstone, o import casa por nome+tipo, o restore apaga tudo
 * antes de reinserir e o join do sync substitui a base.
 *
 * Funde cada grupo repetido na cópia com MAIS uso (empate: a mais antiga), repontando
 * TODAS as referências antes de soft-deletar a perdedora — inclusive as três que o
 * `mergeCategories` da tela não cobre (recorrência, parcelamento, financiamento).
 * Depois desdobra o mesmo tratamento nas subcategorias que a fusão empilhou na
 * sobrevivente, senão "Moradia" terminaria com as 5 subcategorias em dobro.
 *
 * Roda em todo boot em vez de uma vez só com DONE_KEY: a detecção é um GROUP BY numa
 * tabela de algumas dezenas de linhas (sai antes de qualquer escrita quando não há
 * nada a fazer) e assim também cobre uma duplicata que chegue depois — por exemplo do
 * oplog de um aparelho antigo que já estava sujo.
 *
 * O executor vem por parâmetro pelo mesmo motivo do cleanupZombieInvoiceArtifacts:
 * o chamador real é o boot (db/client) e importar o client daqui criaria ciclo.
 */
export async function dedupeSeedCategories(execute: SqlExecutor): Promise<DedupeSeedCategoriesResult> {
    const result: DedupeSeedCategoriesResult = { mergedCategories: 0, mergedSubcategories: 0 };

    // Só o catálogo de FÁBRICA (`is_system = 1`). Duas categorias de mesmo nome criadas
    // à mão são escolha do usuário — o app permite, e fundir por conta própria seria
    // destruir organização deliberada.
    const groups = await execute(
        `SELECT name, type, COUNT(*) AS n
           FROM categories
          WHERE deleted_at IS NULL AND is_system = 1
          GROUP BY name, type
         HAVING n > 1`,
    );
    if (groups.rows.length === 0) return result;

    const now = new Date().toISOString();

    for (const group of groups.rows) {
        // Sobrevivente: a que carrega mais dado do USUÁRIO. O peso soma as fontes que
        // apontam pra categoria (não só transação: um aparelho pode ter caído na cópia
        // "errada" só com recorrência ou orçamento), e de propósito NÃO conta as regras
        // de auto-categorização — elas nascem do seed, ficam todas na cópia que o
        // `ensureDefaultAutoRules` encontrou primeiro e fariam a cópia de fábrica ganhar
        // da que tem os lançamentos. Empate — o caso comum, porque as duas cópias nascem
        // idênticas — resolve pela mais antiga, e o `id` fecha qualquer resto de
        // ambiguidade pra ordenação ser determinística.
        const candidates = await execute(
            `SELECT c.id,
                    (SELECT COUNT(*) FROM transactions t WHERE t.category_id = c.id AND t.deleted_at IS NULL)
                  + (SELECT COUNT(*) FROM recurring_transactions r WHERE r.category_id = c.id AND r.deleted_at IS NULL)
                  + (SELECT COUNT(*) FROM installments i WHERE i.category_id = c.id)
                  + (SELECT COUNT(*) FROM financings f WHERE f.category_id = c.id)
                  + (SELECT COUNT(*) FROM budget_categories b WHERE b.category_id = c.id)
                    AS weight
               FROM categories c
              WHERE c.name = ? AND c.type = ? AND c.deleted_at IS NULL AND c.is_system = 1
              ORDER BY weight DESC, c.created_at ASC, c.id ASC`,
            [group.name, group.type],
        );

        const [survivor, ...losers] = candidates.rows.map((row: any) => row.id as string);
        if (!survivor) continue;

        for (const loser of losers) {
            await repointCategory(execute, loser, survivor, now);
            result.mergedCategories += 1;
        }

        result.mergedSubcategories += await dedupeSubcategoriesOf(execute, survivor, now);
    }

    return result;
}

/** Move tudo que aponta pra `from` e soft-deleta a categoria absorvida. */
async function repointCategory(execute: SqlExecutor, from: string, to: string, now: string): Promise<void> {
    await execute(`UPDATE transactions SET category_id = ?, updated_at = ? WHERE category_id = ?`, [to, now, from]);
    await execute(`UPDATE recurring_transactions SET category_id = ?, updated_at = ? WHERE category_id = ?`, [to, now, from]);
    await execute(`UPDATE installments SET category_id = ?, updated_at = ? WHERE category_id = ?`, [to, now, from]);
    await execute(`UPDATE financings SET category_id = ?, updated_at = ? WHERE category_id = ?`, [to, now, from]);
    await execute(`UPDATE auto_categorization_rules SET category_id = ? WHERE category_id = ?`, [to, from]);
    // Coluna LEGADA de budgets (a verdade é a junction budget_categories desde a
    // migração 0005), mas é NOT NULL e o restore de backup antigo ainda a lê pra
    // reconstruir o vínculo — deixá-la apontando pra categoria morta ressuscitaria a
    // duplicata num restore futuro.
    await execute(`UPDATE budgets SET category_id = ?, updated_at = ? WHERE category_id = ?`, [to, now, from]);
    // Orçamento que já inclui a sobrevivente descarta o link da perdedora (o par
    // (budget, category) é único); os demais são repontados.
    await execute(
        `DELETE FROM budget_categories
          WHERE category_id = ?
            AND budget_id IN (SELECT budget_id FROM budget_categories WHERE category_id = ?)`,
        [from, to],
    );
    await execute(`UPDATE budget_categories SET category_id = ? WHERE category_id = ?`, [to, from]);
    // Sugestão da inbox de notificações: snapshot sem FK, mas apontar pra categoria
    // morta faria o prefill abrir com uma categoria que não existe mais na tela.
    await execute(`UPDATE pending_notifications SET suggested_category_id = ? WHERE suggested_category_id = ?`, [to, from]);
    // As subcategorias vêm junto — quem aponta pra elas (transação, recorrência,
    // orçamento por subcategoria) continua válido sem tocar em nada.
    await execute(`UPDATE subcategories SET category_id = ?, updated_at = ? WHERE category_id = ?`, [to, now, from]);
    await execute(`UPDATE categories SET deleted_at = ?, updated_at = ? WHERE id = ?`, [now, now, from]);
}

/**
 * Funde as subcategorias que a fusão empilhou na sobrevivente. A identidade é o
 * `seed_name` (migração 0031) e, na falta dele, o nome normalizado — assim uma
 * subcategoria RENOMEADA pelo usuário numa das cópias ainda encontra a irmã.
 * Subcategoria criada pelo usuário (`is_system = 0`) fica fora: nome repetido ali é
 * escolha dele.
 */
async function dedupeSubcategoriesOf(execute: SqlExecutor, categoryId: string, now: string): Promise<number> {
    const rows = await execute(
        `SELECT id, COALESCE(seed_name, name) AS identity,
                (SELECT COUNT(*) FROM transactions t WHERE t.subcategory_id = s.id AND t.deleted_at IS NULL)
              + (SELECT COUNT(*) FROM recurring_transactions r WHERE r.subcategory_id = s.id AND r.deleted_at IS NULL)
              + (SELECT COUNT(*) FROM installments i WHERE i.subcategory_id = s.id)
              + (SELECT COUNT(*) FROM financings f WHERE f.subcategory_id = s.id)
              + (SELECT COUNT(*) FROM budget_subcategories b WHERE b.subcategory_id = s.id)
              + (SELECT COUNT(*) FROM budget_subcategory_limits l WHERE l.subcategory_id = s.id)
                AS weight
           FROM subcategories s
          WHERE s.category_id = ? AND s.deleted_at IS NULL AND s.is_system = 1
          ORDER BY weight DESC, s.created_at ASC, s.id ASC`,
        [categoryId],
    );

    const byIdentity = new Map<string, string[]>();
    for (const row of rows.rows) {
        const key = String(row.identity ?? '').trim().toLocaleLowerCase();
        const ids = byIdentity.get(key);
        if (ids) ids.push(row.id);
        else byIdentity.set(key, [row.id]);
    }

    let merged = 0;
    for (const ids of byIdentity.values()) {
        const [survivor, ...losers] = ids;
        for (const loser of losers) {
            await execute(`UPDATE transactions SET subcategory_id = ?, updated_at = ? WHERE subcategory_id = ?`, [survivor, now, loser]);
            await execute(`UPDATE recurring_transactions SET subcategory_id = ?, updated_at = ? WHERE subcategory_id = ?`, [survivor, now, loser]);
            await execute(`UPDATE installments SET subcategory_id = ?, updated_at = ? WHERE subcategory_id = ?`, [survivor, now, loser]);
            await execute(`UPDATE financings SET subcategory_id = ?, updated_at = ? WHERE subcategory_id = ?`, [survivor, now, loser]);
            await execute(`UPDATE auto_categorization_rules SET subcategory_id = ? WHERE subcategory_id = ?`, [survivor, loser]);
            // Recorte de orçamento por subcategoria (ADRs 0067 e 0110): o par com a
            // sobrevivente é único, então o do perdedor é descartado quando já existe.
            await execute(
                `DELETE FROM budget_subcategories
                  WHERE subcategory_id = ?
                    AND budget_id IN (SELECT budget_id FROM budget_subcategories WHERE subcategory_id = ?)`,
                [loser, survivor],
            );
            await execute(`UPDATE budget_subcategories SET subcategory_id = ? WHERE subcategory_id = ?`, [survivor, loser]);
            await execute(
                `DELETE FROM budget_subcategory_limits
                  WHERE subcategory_id = ?
                    AND budget_id IN (SELECT budget_id FROM budget_subcategory_limits WHERE subcategory_id = ?)`,
                [loser, survivor],
            );
            await execute(`UPDATE budget_subcategory_limits SET subcategory_id = ? WHERE subcategory_id = ?`, [survivor, loser]);
            // Soft-delete, não DELETE: a linha de sistema apagada é o TOMBSTONE que
            // impede o `ensureNewCategories` de replantar o nome no próximo boot.
            await execute(`UPDATE subcategories SET deleted_at = ?, updated_at = ? WHERE id = ?`, [now, now, loser]);
            merged += 1;
        }
    }

    return merged;
}
